import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useAuth } from '../modules/auth';
import { useTheme } from '../context/ThemeContext';
import {
  getActiveAnnouncements,
  dismissAnnouncement,
} from '../services/announcements';
import { ActiveAnnouncement } from '../modules/super-admin/types';

const POLL_INTERVAL_MS = 10 * 60 * 1000; // 10 min, per spec §4

const isExternalUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin !== window.location.origin;
  } catch {
    return false;
  }
};

interface AnnouncementCardProps {
  announcement: ActiveAnnouncement;
  dark: boolean;
  onDismiss: (id: string) => void;
}

// One announcement rendered as a vertical card inside the modal (title, body,
// optional CTA, optional dismiss) — modal width is generous so this no longer
// needs the cramped horizontal banner layout.
const AnnouncementCard: React.FC<AnnouncementCardProps> = ({
  announcement,
  dark,
  onDismiss,
}) => {
  const { id, title, body, ctaUrl, ctaLabel, dismissible } = announcement;

  const bgColor = dark ? '#1d2b3a' : '#f8fafc';
  const borderColor = dark ? '#3a4a5e' : '#e2e8f0';
  const textColor = dark ? '#e5e7eb' : '#1e293b';
  const subTextColor = dark ? '#cbd5e1' : '#475569';

  return (
    <div
      className="rounded p-4 mb-3"
      style={{
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
        color: textColor,
      }}
    >
      <div className="d-flex align-items-start justify-content-between gap-3">
        <div className="fw-bold fs-6" style={{ color: textColor }}>
          {title}
        </div>
        {dismissible && (
          <button
            type="button"
            aria-label="Dismiss announcement"
            className="btn btn-icon btn-sm btn-active-light-primary flex-shrink-0"
            onClick={() => onDismiss(id)}
            style={{ color: subTextColor }}
          >
            <i className="bi bi-x-lg" />
          </button>
        )}
      </div>
      <div
        className="fs-7 mt-1"
        style={{ color: subTextColor, whiteSpace: 'pre-wrap' }}
      >
        {body}
      </div>
      {ctaUrl && ctaLabel && (
        <a
          href={ctaUrl}
          target={isExternalUrl(ctaUrl) ? '_blank' : undefined}
          rel={isExternalUrl(ctaUrl) ? 'noopener noreferrer' : undefined}
          className="btn btn-sm btn-primary mt-3"
        >
          {ctaLabel}
        </a>
      )}
    </div>
  );
};

export const AnnouncementBanner: React.FC = () => {
  const { currentUser } = useAuth();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const [items, setItems] = useState<ActiveAnnouncement[]>([]);
  const [showModal, setShowModal] = useState(false);
  // Track ids the user dismissed in this session so the next poll doesn't
  // briefly re-render them before the backend's join-table reflects the
  // dismissal. The set survives polls for the lifetime of the component.
  const localDismissedRef = useRef<Set<string>>(new Set());

  // Subscription/upgrade flows call setCurrentUser repeatedly with new object
  // identities for the same user, which would otherwise re-fire fetchActive
  // on every auth churn and pile API calls + state updates on top of an
  // in-flight react-bootstrap Tab transition. Dep on the stable user id only.
  const userId: string | undefined = currentUser?.data?.user?.id;
  // Invited org members don't own a subscription and aren't the audience for
  // owner-facing announcements; hide the entry point entirely for them.
  const isInvitedMember = !!currentUser?.data?.user?.isInvitedByOrganization;

  const fetchActive = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await getActiveAnnouncements();
      const list: ActiveAnnouncement[] =
        res.data?.data?.announcements ?? res.data?.announcements ?? [];
      const filtered = list.filter((a) => !localDismissedRef.current.has(a.id));
      setItems(filtered);
    } catch (err) {
      // Silent — announcements are non-critical UI; logging keeps the console
      // useful for ops without surfacing a toast on every transient blip.
      // eslint-disable-next-line no-console
      console.error('Failed to fetch active announcements:', err);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId || isInvitedMember || currentUser?.data?.user?.role.isSuperAdmin) {
      setItems([]);
      return;
    }
    fetchActive();
    // Visible-tab-only polling. Pause the interval on hidden tabs to avoid
    // burning cycles on backgrounded windows; refetch immediately when the
    // tab becomes visible again so a returning user sees current state.
    let timer: number | null = null;
    const start = () => {
      if (timer != null) return;
      timer = window.setInterval(fetchActive, POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (timer != null) {
        window.clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchActive();
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [userId, isInvitedMember, fetchActive]);

  const handleDismiss = useCallback(async (id: string) => {
    localDismissedRef.current.add(id);
    setItems((prev) => prev.filter((a) => a.id !== id));
    try {
      await dismissAnnouncement(id);
    } catch (err: any) {
      const status = err?.response?.status;
      // Spec §4: 404 means the announcement is already gone — treat as success.
      // 400 with "cannot be dismissed" shouldn't reach here (X is hidden when
      // dismissible is false), but if it does, log and let the next poll fix it.
      if (status === 404) return;
      // eslint-disable-next-line no-console
      console.error('Failed to dismiss announcement:', err);
    }
  }, []);

  // Highest priority first so the most important announcement leads the list;
  // missing priority sorts as 0. Ties break on the soonest expiry so
  // time-sensitive notices surface first.
  const sorted = useMemo(() => {
    const prio = (a: ActiveAnnouncement) =>
      typeof a.priority === 'number' ? a.priority : 0;
    return [...items].sort((a, b) => {
      if (prio(b) !== prio(a)) return prio(b) - prio(a);
      return new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime();
    });
  }, [items]);

  if (!userId || isInvitedMember) return null;

  const count = items.length;

  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className="btn btn-custom w-100 text-white d-flex align-items-center justify-content-center"
        title="View announcements"
      >
        <span className="btn-label text-white d-flex align-items-center fs-6">
          <i className="bi bi-megaphone fs-6 text-white" />
          Announcements
        </span>
        {count > 0 && (
          <span className="badge badge-circle badge-light-primary ms-2">
            {count}
          </span>
        )}
      </button>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        scrollable
        contentClassName={dark ? 'app-download-modal-dark' : ''}
      >
        <Modal.Header className={dark ? 'app-download-modal-dark' : ''}>
          <Modal.Title className={dark ? 'white__text' : ''}>
            Announcements{count > 0 ? ` (${count})` : ''}
          </Modal.Title>
          <i
            className={`bi bi-x-lg modal-icon-custom ${dark ? 'close' : ''}`}
            onClick={() => setShowModal(false)}
            role="button"
            aria-label="Close"
          />
        </Modal.Header>
        <Modal.Body className={dark ? 'app-download-modal-dark' : ''}>
          {sorted.length === 0 ? (
            <div
              className="text-center py-8"
              style={{ color: dark ? '#9ca3af' : '#7e8299' }}
            >
              <i className="bi bi-megaphone fs-2x d-block mb-3 opacity-50" />
              You&rsquo;re all caught up — no announcements right now.
            </div>
          ) : (
            sorted.map((a) => (
              <AnnouncementCard
                key={a.id}
                announcement={a}
                dark={dark}
                onDismiss={handleDismiss}
              />
            ))
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AnnouncementBanner;
